<template>
    <v-dialog v-model="modal" :scrim="false" :persistent="loading">
        <v-card>
            <v-card-title class="text-white bg-primary">
                {{ serviceDetails.activityName }}
            </v-card-title>
            <v-card-text v-if="bookingFieldsLoading" class="d-flex full-width align-center justify-center pa-8">
                <v-progress-circular color="primary" indeterminate size="64" />
            </v-card-text>
            <template v-else>
                <v-card-text>
                    <div class="my-5">
                        <div v-for="(tourist, index) in tourists" :key="index" :data-cy="`tourist-${index}`">
                            <tourist-form
                                :ref="addFormRef"
                                v-model="tourists[index]"
                                :index="index"
                                label="passenger"
                                :disabled="loading"
                                :hide-index="tourists.length === 1"
                                :selected-tourists="tourists"
                                :start-date="startDate"
                                full-width
                                @person-selected="personSelected($event)"
                            />
                            <tourist-passport-form
                                :ref="addFormRef"
                                :index="index"
                                :disabled="loading"
                                expanded-all
                                @set-prop="onTouristsChange"
                            />
                        </div>
                    </div>
                    <activity-booking-form
                        :ref="addFormRef"
                        v-model="activityFormValid"
                        :info="info"
                        :start-time="service.serviceDetails[0].pickupTime"
                        :disabled="loading"
                        :pickup-location="service.serviceDetails[0].pickupLocation"
                        @change="setPickupPointField"
                    />
                </v-card-text>
                <v-card-actions>
                    <v-btn color="primary" :disabled="loading" :loading="loading" @click="save">
                        {{ $t('save') }}
                    </v-btn>
                    <v-btn variant="text" :disabled="loading" @click="modal = false">{{ $t('cancel') }}</v-btn>
                </v-card-actions>
            </template>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
    import {Component, mixins, Prop, VModel, Watch} from 'vue-facing-decorator'
    import {BookingTouristsMixin} from '~src/components/booking/mixins/BookingTouristsMixin.vue'
    import TouristForm from '~src/components/booking/forms/touristForm.src'
    import {ActivityPageMixin} from '@/components/activities/mixins/ActivityPageMixin.vue'
    import {FillServiceBookingDataMixin} from '@/components/account/mixins/FillServiceBookingDataMixin.vue'
    import ActivityBookingForm from '@/components/activities/booking/forms/ActivityBookingForm.vue'
    import {clone} from '@/utils/helpers'
    import TouristPassportForm from '~src/components/booking/forms/touristPassportForm.src'

    //TODO Add train stations
    @Component({
        components: {
            TouristPassportForm,
            ActivityBookingForm,
            TouristForm,
        },
    })
    export class FillActivityBookingDataModal extends mixins(
        BookingTouristsMixin,
        ActivityPageMixin,
        FillServiceBookingDataMixin
    ) {
        @VModel() modal
        @Prop({required: true}) service
        @Prop({required: true}) order
        @Prop({required: true}) info

        loading = false
        activityFormValid = false

        @Watch('modal')
        async onModalOpen() {
            if (this.modal) {
                this.setTourists()
                await Promise.all([this.loadTouristBookingFields(), this.loadProductBookingFields()])
                await this.$nextTick()
                this.allNestedFormsValid = await this.validateNestedForms(this)
            }
        }

        async mounted() {
            await this.$store.restored
            await this.loadRuntimeData()
        }

        async save() {
            this.allNestedFormsValid = await this.validateNestedForms(this)
            if (!this.allNestedFormsValid || !this.activityFormValid) return
            try {
                this.loading = true

                await this.$api.fillActivityBookingData.post({
                    tourists: this.prepareBookTourists(this.tourists),
                    processId: this.service.processId,
                    pickUpPlace: activitiesStore.prepareBookRequest.pickUpPlace,
                    hotelPointKey: activitiesStore.prepareBookRequest.hotelPointKey,
                    startTime: activitiesStore.prepareBookRequest.startTime,
                })
                await runtimeStore.refreshOrder()
            } catch (e) {
                this.$toast.error(this.$t('error_message.save_error'))
            } finally {
                this.modal = false
                this.loading = false
            }
        }

        setPickupPointField({prop, val}) {
            const rqCopy = clone(activitiesStore.prepareBookRequest)

            if (!val) {
                delete rqCopy[prop]
            }

            activitiesStore.SET_PREPARE_BOOK_REQUEST({
                ...rqCopy,
                ...(val && {[prop]: val}),
            })
        }

        personSelected({person, index}) {
            if (person) {
                const val = runtimeStore.touristFromPerson(person)
                persistentStore.SET_TOURIST({index, val})
            } else {
                persistentStore.SET_TOURIST_PROP({index, prop: 'personId', val: null})
            }
        }

        get serviceDetails() {
            return this.service.serviceDetails[0]
        }

        get searchRequest() {
            return {
                dateTime: this.service.startDateTime.split(' ')[0],
                adults: this.service.travelers.filter(({isChild}) => !isChild).length,
                childrenAges: this.service.travelers.reduce((childrenAges, {isChild, dateOfBirth, age}) => {
                    if (age !== undefined) {
                        childrenAges.push(age)
                    } else if (isChild) {
                        if (dateOfBirth) {
                            childrenAges.push(
                                this.$dateFns.differenceInYears(
                                    new Date(this.service.startDateTime),
                                    this.$dateFns.parseISO(dateOfBirth)
                                )
                            )
                        } else {
                            childrenAges.push(4)
                        }
                    }
                    return childrenAges
                }, []),
            }
        }

        get startDate() {
            return this.searchRequest.dateTime
        }
    }

    export default FillActivityBookingDataModal
</script>
